import React, { useEffect, useRef, useState } from "react";

/**
 * Premium custom cursor.
 *  - Small solid dot follows mouse exactly (translate3d, no lag).
 *  - Outer ring follows via rAF + lerp for buttery smooth trailing.
 *  - Grows on hover over interactive elements.
 *  - Micro-scale on mousedown.
 *  - Disabled on touch / coarse pointer devices.
 *  - pointer-events: none, z-index: 99999.
 */
export default function CustomCursor() {
  const [enabled, setEnabled] = useState(false);
  const dotRef = useRef(null);
  const ringRef = useRef(null);

  useEffect(() => {
    // Bail on touch or coarse-pointer devices
    const coarse = window.matchMedia("(hover: none), (pointer: coarse)").matches;
    if (coarse || navigator.maxTouchPoints > 0) return;

    setEnabled(true);
    document.documentElement.classList.add("has-custom-cursor");

    const st = {
      mx: window.innerWidth / 2,
      my: window.innerHeight / 2,
      rx: window.innerWidth / 2,
      ry: window.innerHeight / 2,
      scale: 1,
      hovering: false,
      down: false,
      visible: false,
    };

    const onMove = (e) => {
      st.mx = e.clientX;
      st.my = e.clientY;
      if (!st.visible) {
        // first appearance — snap ring to mouse
        st.rx = st.mx;
        st.ry = st.my;
        st.visible = true;
        if (dotRef.current) dotRef.current.style.opacity = "1";
        if (ringRef.current) ringRef.current.style.opacity = "1";
      }
    };

    const onOver = (e) => {
      const el = e.target?.closest?.(
        'a, button, [role="button"], input, textarea, select, label, [data-cursor="hover"], [data-testid$="-btn"]',
      );
      st.hovering = !!el;
    };
    const onDown = () => { st.down = true; };
    const onUp = () => { st.down = false; };
    const onLeave = () => {
      st.visible = false;
      if (dotRef.current) dotRef.current.style.opacity = "0";
      if (ringRef.current) ringRef.current.style.opacity = "0";
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseover", onOver, { passive: true });
    window.addEventListener("mousedown", onDown, { passive: true });
    window.addEventListener("mouseup", onUp, { passive: true });
    document.addEventListener("mouseleave", onLeave, { passive: true });

    let raf = 0;
    const loop = () => {
      // Lerp outer ring toward pointer (0.18 = smooth, 1.0 = instant)
      st.rx += (st.mx - st.rx) * 0.2;
      st.ry += (st.my - st.ry) * 0.2;

      const targetScale = st.down ? 0.72 : st.hovering ? 1.55 : 1;
      st.scale += (targetScale - st.scale) * 0.22;

      const dot = dotRef.current;
      const ring = ringRef.current;
      if (dot) {
        dot.style.transform = `translate3d(${st.mx - 4}px, ${st.my - 4}px, 0)`;
      }
      if (ring) {
        ring.style.transform = `translate3d(${st.rx - 20}px, ${st.ry - 20}px, 0) scale(${st.scale})`;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onOver);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      document.removeEventListener("mouseleave", onLeave);
      document.documentElement.classList.remove("has-custom-cursor");
    };
  }, []);

  if (!enabled) return null;
  return (
    <>
      <div ref={ringRef} className="cursor-ring" aria-hidden="true" />
      <div ref={dotRef} className="cursor-dot" aria-hidden="true" />
    </>
  );
}
